import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { Menu, LogOut, CircleUserRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useOnline } from '@/hooks/use-online'
import { useSession } from '@/features/auth/use-session'
import { useSessionExpired } from '@/features/auth/use-session-expired'
import { useLogout } from '@/features/auth/use-auth-actions'
import { SidebarContent } from './sidebar-content'
import { NavigationDrawer } from './navigation-drawer'
import { ConnectionStatus } from './connection-status'
import { useUIStore } from './ui-store'
import { DashboardHeader } from './dashboard-header'
import '@fontsource/sarabun/400.css'
import '@fontsource/sarabun/600.css'
import '@fontsource/sarabun/700.css'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import '@fontsource/inter/900.css'
import '@/features/dashboard/dashboard.css'

export function AppShell({
  children,
  title,
  dashboard,
  pos = false,
  inventory = false,
  underConstruction = false,
  contentKey,
}: {
  children: ReactNode
  title: string
  dashboard?: { search: string; onSearch: (value: string) => void }
  pos?: boolean
  inventory?: boolean
  underConstruction?: boolean
  contentKey?: string
}) {
  const frame = useRef<HTMLElement>(null)
  useLayoutEffect(() => {
    frame.current?.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [contentKey])
  const online = useOnline()
  const session = useSession()
  const expired = useSessionExpired(session.data?.expiresAt)
  const logout = useLogout()
  const verified = online && session.isSuccess && !expired
  const collapsed = useUIStore((state) =>
    pos ? state.posSidebarCollapsed : state.sidebarCollapsed,
  )
  return (
    <div
      className={`app-layout ${dashboard ? `dashboard-shell ${collapsed ? 'sidebar-collapsed' : ''} ${pos ? 'pos-shell' : ''} ${inventory ? 'inventory-shell' : ''} ${underConstruction ? 'under-construction-shell' : ''}` : ''}`}
    >
      <a className="skip-link" href="#main">
        ข้ามไปเนื้อหา
      </a>
      <aside className="desktop-sidebar">
        <SidebarContent dashboard={Boolean(dashboard)} pos={pos} />
      </aside>
      <NavigationDrawer dashboard={Boolean(dashboard)} pos={pos} />
      <div className="app-body">
        {dashboard ? (
          <DashboardHeader
            search={dashboard.search}
            onSearch={dashboard.onSearch}
            session={verified ? session.data : undefined}
            onLogout={() =>
              session.data && logout.mutate(session.data.csrfToken)
            }
            loggingOut={logout.isPending}
          />
        ) : (
          <header className="app-header">
            <div className="header-leading">
              <Button
                variant="ghost"
                size="icon"
                className="mobile-menu"
                id="mobile-menu-trigger"
                aria-label="เปิดเมนู"
                onClick={() => useUIStore.getState().setDrawerOpen(true)}
              >
                <Menu size={22} />
              </Button>
              <span className="header-breadcrumb">
                พื้นที่ทำงาน <span>/</span> <strong>{title}</strong>
              </span>
            </div>
            <div className="header-trailing">
              <ConnectionStatus />
              <span className="header-divider" />
              {verified ? (
                <>
                  <span className="user-chip">
                    <CircleUserRound size={22} />
                    <span>{session.data.user.displayName}</span>
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="ออกจากระบบ"
                    title="ออกจากระบบ"
                    disabled={logout.isPending}
                    onClick={() => logout.mutate(session.data.csrfToken)}
                  >
                    <LogOut size={18} />
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" asChild>
                  <Link to="/login">เข้าสู่ระบบ</Link>
                </Button>
              )}
            </div>
          </header>
        )}
        <main
          id="main"
          ref={frame}
          tabIndex={-1}
          aria-label={title}
          className={dashboard ? 'content-frame' : 'page-content'}
        >
          {dashboard ? (
            <div key={contentKey} className="page-content route-content">
              {children}
            </div>
          ) : (
            children
          )}
        </main>
        {!dashboard && (
          <footer className="app-footer">
            <span>Pare Pharmacy · ดูแลด้วยความใส่ใจ</span>
            <span>Foundation v0.1</span>
          </footer>
        )}
      </div>
    </div>
  )
}
